import React from 'react';
import { formatCedi } from '../utils/formatters';
import { methodLabels } from '../utils/constants';

const methodIcon = (method) => (method === 'momo' ? '📱' : method === 'cash' ? '💵' : method === 'card' ? '💳' : '🔀');
const methodName = (method) => methodLabels[method] || method;

const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (ch) => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[ch]));

const longDate = (dateStr) => new Date(`${dateStr}T12:00:00`).toLocaleDateString('en-GH', {
  weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
});

const staffMethodSummary = (byMethod = {}) => Object.entries(byMethod)
  .sort((a, b) => b[1] - a[1])
  .map(([method, amount]) => `${methodName(method)} ${formatCedi(amount)}`)
  .join(' · ');

// salesOnly leaves out expenses and profit, for staff who reconcile the till
// but do not see the restaurant's financials.
export const printEodReport = (r, { salesOnly = false } = {}) => {
  const w = window.open('', 'EOD_PRINT', 'height=800,width=700');
  if (!w) return;
  const kpis = [
    ['Total Sales', `GHS ${r.totalRevenue.toFixed(2)}`],
    ...(salesOnly ? [] : [['Net Profit', `GHS ${r.netProfit.toFixed(2)}`, 'profit']]),
    ['Total Orders', r.totalOrders],
    ['Completed Orders', r.completedOrders],
    ['Avg Order Value', `GHS ${r.avgOrderValue.toFixed(2)}`],
    ...(salesOnly ? [] : [['Total Expenses', `GHS ${r.totalExpenses.toFixed(2)}`]]),
  ];
  const staffRows = Object.entries(r.byStaff)
    .sort((a, b) => b[1].revenue - a[1].revenue)
    .map(([name, d]) => `<tr><td>${escapeHtml(name)}</td><td>${d.orders}</td><td>${escapeHtml(staffMethodSummary(d.byMethod))}</td><td>GHS ${d.revenue.toFixed(2)}</td></tr>`)
    .join('');
  w.document.write(`<html><head><title>${salesOnly ? 'End-of-Day Sales' : 'EOD Report'} - ${escapeHtml(r.dateStr)}</title>
    <style>body{font-family:Arial,sans-serif;font-size:13px;padding:20px;color:#111}h1{font-size:22px;margin-bottom:4px}h2{font-size:16px;margin:16px 0 8px;border-bottom:1px solid #ccc;padding-bottom:4px}.grid2{display:grid;grid-template-columns:1fr 1fr;gap:12px}.kpi{background:#f8f8f8;border-radius:8px;padding:12px}.kpi .label{font-size:11px;color:#666;text-transform:uppercase}.kpi .value{font-size:20px;font-weight:bold;margin-top:2px}table{width:100%;border-collapse:collapse;margin-top:6px}th{text-align:left;font-size:11px;text-transform:uppercase;color:#666;padding:6px 8px;border-bottom:1px solid #eee}td{padding:6px 8px;border-bottom:1px solid #f0f0f0}.profit{color:${r.netProfit >= 0 ? '#16a34a' : '#dc2626'}}@media print{button{display:none}}</style></head><body>
    <h1>📋 ${salesOnly ? 'End-of-Day Sales' : 'End-of-Day Report'}</h1>
    <div style="color:#666;margin-bottom:16px;">Komis Kenkey &mdash; ${longDate(r.dateStr)}</div>
    <div class="grid2">${kpis.map(([label, value, cls]) => `<div class="kpi"><div class="label">${label}</div><div class="value ${cls || ''}">${value}</div></div>`).join('')}</div>
    <h2>Payment Breakdown</h2>
    <table><thead><tr><th>Method</th><th>Amount</th></tr></thead><tbody>${Object.entries(r.byMethod).map(([m, a]) => `<tr><td>${escapeHtml(methodName(m))}</td><td>GHS ${a.toFixed(2)}</td></tr>`).join('')}</tbody></table>
    <h2>Sales by Staff</h2>
    <table><thead><tr><th>Staff</th><th>Orders</th><th>By method</th><th>Total</th></tr></thead><tbody>${staffRows}</tbody></table>
    <h2>Top Selling Items</h2>
    <table><thead><tr><th>Item</th><th>Qty Sold</th><th>Revenue</th></tr></thead><tbody>${r.topItems.map(([name, d]) => `<tr><td>${escapeHtml(name)}</td><td>${d.qty}</td><td>GHS ${d.revenue.toFixed(2)}</td></tr>`).join('')}</tbody></table>
    <div style="margin-top:24px;padding-top:12px;border-top:1px solid #ccc;color:#888;font-size:11px;text-align:center;">Generated by Komis POS &mdash; ${new Date().toLocaleString()}</div>
    <script>window.print();setTimeout(()=>window.close(),600);</script></body></html>`);
  w.document.close();
};

export default function EodReport({ report: r, salesOnly = false }) {
  const kpis = [
    { label: salesOnly ? 'Total Sales' : 'Total Revenue', value: formatCedi(r.totalRevenue), color: 'text-green-700', bg: 'bg-green-50', border: 'border-green-200', icon: '💰' },
    ...(salesOnly ? [] : [
      { label: 'Net Profit', value: formatCedi(r.netProfit), color: r.netProfit >= 0 ? 'text-emerald-700' : 'text-red-700', bg: r.netProfit >= 0 ? 'bg-emerald-50' : 'bg-red-50', border: r.netProfit >= 0 ? 'border-emerald-200' : 'border-red-200', icon: '📈' },
      { label: 'Total Expenses', value: formatCedi(r.totalExpenses), color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200', icon: '💸' },
    ]),
    { label: 'Total Orders', value: r.totalOrders, color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200', icon: '📦' },
    { label: 'Completed', value: r.completedOrders, color: 'text-purple-700', bg: 'bg-purple-50', border: 'border-purple-200', icon: '✅' },
    { label: 'Avg Order', value: formatCedi(r.avgOrderValue), color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', icon: '📊' },
  ];
  const methodTotal = Object.values(r.byMethod).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-6">
      <div className="text-center">
        <p className="text-lg font-semibold text-gray-700">{longDate(r.dateStr)}</p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {kpis.map(k => (
          <div key={k.label} className={`rounded-xl p-4 border ${k.bg} ${k.border}`}>
            <div className="text-2xl mb-1">{k.icon}</div>
            <div className="text-xs text-gray-500 font-medium uppercase tracking-wide">{k.label}</div>
            <div className={`text-xl font-black mt-0.5 ${k.color}`}>{k.value}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
          <div className="text-3xl font-black text-blue-700">{r.onlineOrders}</div>
          <div className="text-xs text-blue-600 font-semibold mt-1">🛵 Online Orders</div>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 text-center">
          <div className="text-3xl font-black text-purple-700">{r.inPersonOrders}</div>
          <div className="text-xs text-purple-600 font-semibold mt-1">🏃 Walk-in Orders</div>
        </div>
      </div>
      <div>
        <h3 className="font-bold text-gray-800 mb-3">💳 Payment Breakdown</h3>
        {Object.keys(r.byMethod).length === 0 ? (
          <p className="text-gray-400 text-sm">No completed orders for this date.</p>
        ) : (
          <div className="space-y-2">
            {Object.entries(r.byMethod).map(([method, amount]) => {
              const pct = methodTotal > 0 ? (amount / methodTotal) * 100 : 0;
              return (
                <div key={method} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <span className="text-lg">{methodIcon(method)}</span>
                  <div className="flex-1">
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-semibold text-gray-700">{methodName(method)}</span>
                      <span className="text-sm font-bold text-gray-800">{formatCedi(amount)}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div className="bg-indigo-500 h-1.5 rounded-full" style={{ width: `${pct}%` }}></div>
                    </div>
                  </div>
                  <span className="text-xs text-gray-500 w-10 text-right">{pct.toFixed(0)}%</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {Object.keys(r.byStaff).length > 0 && (
        <div>
          <h3 className="font-bold text-gray-800 mb-3">👤 Sales by Staff</h3>
          <div className="space-y-2">
            {Object.entries(r.byStaff).sort((a, b) => b[1].revenue - a[1].revenue).map(([name, data]) => (
              <div key={name} className="flex items-center justify-between gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                <div className="min-w-0">
                  <div className="font-semibold text-gray-800 text-sm">{name}</div>
                  <div className="text-xs text-gray-500">{data.orders} orders</div>
                  {data.byMethod && Object.keys(data.byMethod).length > 0 && (
                    <div className="text-xs text-gray-600 mt-0.5">{staffMethodSummary(data.byMethod)}</div>
                  )}
                </div>
                <div className="text-right shrink-0"><div className="font-bold text-blue-700 text-sm">{formatCedi(data.revenue)}</div><div className="text-xs text-gray-400">avg {formatCedi(data.orders > 0 ? data.revenue / data.orders : 0)}</div></div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div>
        <h3 className="font-bold text-gray-800 mb-3">🏆 Top Selling Items</h3>
        {r.topItems.length === 0 ? (
          <p className="text-gray-400 text-sm">No sales data for this date.</p>
        ) : (
          <div className="space-y-2">
            {r.topItems.map(([name, data], idx) => (
              <div key={name} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white ${idx === 0 ? 'bg-yellow-500' : idx === 1 ? 'bg-gray-400' : idx === 2 ? 'bg-amber-700' : 'bg-gray-300'}`}>{idx + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-gray-800 text-sm truncate">{name}</div>
                  <div className="text-xs text-gray-500">{data.qty} sold</div>
                </div>
                <div className="text-sm font-bold text-green-700">{formatCedi(data.revenue)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
      {!salesOnly && r.dayExpenses.length > 0 && (
        <div>
          <h3 className="font-bold text-gray-800 mb-3">💸 Expenses Today</h3>
          <div className="space-y-2">
            {r.dayExpenses.map(exp => (
              <div key={exp.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-100">
                <div><div className="font-semibold text-gray-800 text-sm">{exp.category}</div>{exp.description && <div className="text-xs text-gray-500">{exp.description}</div>}</div>
                <div className="text-sm font-bold text-red-700">{formatCedi(exp.amount)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
        <div className="text-center">
          <div className="text-sm text-indigo-600 font-semibold mb-1">Day Summary</div>
          <div className="text-4xl font-black text-indigo-700 mb-1">{formatCedi(r.totalRevenue)}</div>
          {salesOnly ? (
            <div className="text-sm text-gray-600">Sales from {r.completedOrders} completed orders</div>
          ) : (
            <div className="text-sm text-gray-600">Revenue — <span className={`font-bold ${r.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCedi(r.netProfit)} profit</span> after expenses</div>
          )}
        </div>
      </div>
    </div>
  );
}
